import { useParams } from "@tanstack/react-router";

import { RequireCapability } from "@/components/RequireCapability";

import { FormationGroupCreate } from "./components/FormationGroupCreate";
import { FormationGroupDetail } from "./components/FormationGroupDetail";
import { FormationGroupEdit } from "./components/FormationGroupEdit";
import { FormationGroupList } from "./components/FormationGroupList";
import { ParticipantDataView } from "./components/ParticipantDataView";
import { ParticipantProfileForm } from "./components/ParticipantProfileForm";
// Registers the slice's formationGroups.* translations (side-effect import).
import "./i18n";

/**
 * The CQDD training groups area.
 *
 * Every page is gated on `backOffice`, which reads the API's `is_admin`
 * permission — the same rule as `api.permissions.IsAdminUser`, so a regional
 * partner lands on Forbidden here and is refused by the endpoint behind it.
 * The gate is not the protection; the endpoint is. This just avoids offering a
 * dead affordance.
 */
const ADMIN_ONLY = ["backOffice"] as const;

export function FormationGroupsPage() {
  return (
    <RequireCapability required={ADMIN_ONLY}>
      <FormationGroupList />
    </RequireCapability>
  );
}

export function FormationGroupCreatePage() {
  return (
    <RequireCapability required={ADMIN_ONLY}>
      <FormationGroupCreate />
    </RequireCapability>
  );
}

/** Route params are read here, the way the expert pages do it. */
function useRouteIds() {
  const params = useParams({ strict: false }) as {
    id?: string;
    profileId?: string;
  };
  return { groupId: params.id ?? "", profileId: params.profileId ?? "" };
}

export function FormationGroupDetailPage() {
  const { groupId } = useRouteIds();
  return (
    <RequireCapability required={ADMIN_ONLY}>
      <FormationGroupDetail id={groupId} />
    </RequireCapability>
  );
}

export function FormationGroupEditPage() {
  const { groupId } = useRouteIds();
  return (
    <RequireCapability required={ADMIN_ONLY}>
      <FormationGroupEdit id={groupId} />
    </RequireCapability>
  );
}

export function FormationGroupParticipantPage() {
  const { groupId, profileId } = useRouteIds();
  return (
    <RequireCapability required={ADMIN_ONLY}>
      <ParticipantDataView groupId={groupId} profileId={profileId} />
    </RequireCapability>
  );
}

export function FormationGroupParticipantProfilePage() {
  const { groupId, profileId } = useRouteIds();
  return (
    <RequireCapability required={ADMIN_ONLY}>
      <ParticipantProfileForm groupId={groupId} profileId={profileId} />
    </RequireCapability>
  );
}
